import { createFileRoute, Link } from "@tanstack/react-router";
import { redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { fetchProductsByCollection } from "@/lib/shopify";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import {
  ProductFilters,
  ActiveFilterChips,
  deriveFilterFacets,
  applyFilters,
  applySort,
  type FilterState,
  type SortKey,
} from "@/components/ProductFilters";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronRight } from "lucide-react";
import heroTheField from "@/assets/hero-the-field.jpg";
import heroWestern from "@/assets/hero-western.jpg";
import heroLifestyle from "@/assets/hero-lifestyle.jpg";

const collectionMeta: Record<string, { title: string; description: string; handle: string; tag?: string; hero: string }> = {
  "all-hats": {
    title: "All Hats",
    description: "Browse every Coyote Creek hat — structured trucker caps, western crowns, and field-ready lids built to last.",
    handle: "all-hats",
    hero: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Shopify_Slideshow_Images_79834561-dfa1-4cb0-b0cd-1d0f69caaa4b.png?v=1765205010",
  },
  "new-gear": {
    title: "New Gear",
    description: "Fresh Coyote Creek releases built for the sporting life — new hats, tops, and everyday goods.",
    handle: "new-gear",
    hero: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Shopify_Slideshow_Images_79834561-dfa1-4cb0-b0cd-1d0f69caaa4b.png?v=1765205010",
  },
  tops: {
    title: "Tops",
    description: "Premium tees and tops built for the sporting life.",
    handle: "tops",
    hero: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/02E654D6-940D-48A1-857D-CC68FBD02DF3.jpg?v=1725217582",
  },
  "the-field": { title: "The Field", description: "Built for the hunt. Rugged caps designed for the outdoorsman.", handle: "all-hats", tag: "Field", hero: heroTheField },
  western: { title: "Western", description: "Rooted in tradition. Classic western style with modern edge.", handle: "all-hats", tag: "Western", hero: heroWestern },
  lifestyle: { title: "Lifestyle", description: "From the fairway to Friday nights. Casual style, premium quality.", handle: "all-hats", tag: "Lifestyle", hero: heroLifestyle },
};

export const Route = createFileRoute("/collections/$collection")({
  beforeLoad: ({ params }) => {
    if (params.collection === "all") {
      throw redirect({
        to: "/collections/$collection",
        params: { collection: "all-hats" },
        statusCode: 301,
      });
    }
  },
  head: ({ params }) => {
    const meta = collectionMeta[params.collection] || collectionMeta["all-hats"];
    const url = `https://coyotecreekhatco.com/collections/${params.collection}`;
    return {
      meta: [
        { title: `${meta.title} | Coyote Creek Hat Co.` },
        { name: "description", content: meta.description },
        { property: "og:title", content: `${meta.title} | Coyote Creek Hat Co.` },
        { property: "og:description", content: meta.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        ...(meta.hero.startsWith("https://")
          ? [
              { property: "og:image", content: meta.hero },
              { name: "twitter:image", content: meta.hero },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${meta.title} | Coyote Creek Hat Co.`,
          description: meta.description,
          url,
          isPartOf: { "@type": "WebSite", name: "Coyote Creek Hat Co.", url: "https://coyotecreekhatco.com" },
        }),
      }],
    };
  },
  component: CollectionPage,
});

function CollectionPage() {
  const { collection } = Route.useParams();
  const meta = collectionMeta[collection] || collectionMeta["all-hats"];

  const { data: products, isLoading } = useQuery({
    queryKey: ["products", "collection", collection, meta.handle, meta.tag ?? "all"],
    queryFn: async () => {
      const items = await fetchProductsByCollection(meta.handle, 50);
      if (!meta.tag) return items;
      return items.filter((p) => p.node.tags?.some((tag) => tag.toLowerCase() === meta.tag?.toLowerCase()));
    },
  });

  const facets = useMemo(() => deriveFilterFacets(products ?? []), [products]);
  const [filters, setFilters] = useState<FilterState>({
    inStockOnly: false,
    colors: [],
    sizes: [],
    priceRange: [0, 500],
  });
  const [sort, setSort] = useState<SortKey>("featured");

  useEffect(() => {
    setFilters((f) => ({ ...f, priceRange: [facets.priceMin, facets.priceMax] }));
  }, [facets.priceMin, facets.priceMax]);

  const filtered = useMemo(() => applyFilters(products ?? [], filters), [products, filters]);
  const sorted = useMemo(() => applySort(filtered, sort), [filtered, sort]);

  return (
    <div>
      <div className="relative h-[50vh] min-h-[320px] w-full overflow-hidden">
        <img src={meta.hero} alt={`${meta.title} collection — Coyote Creek Hat Co. premium goods`} className="absolute inset-0 h-full w-full object-cover" width={1920} height={768} />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="px-6 text-center font-heading text-5xl uppercase tracking-[0.15em] text-white drop-shadow-lg md:text-7xl">{meta.title}</h1>
        </div>
      </div>

      <div className="border-b border-[#D4CCBC] bg-[#FDFAF3]">
        <div className="mx-auto max-w-[1400px] px-6 py-3">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 font-ui text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8B6F47]">
            <Link to="/" className="hover:text-[#FA5021]">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/collections/$collection" params={{ collection: "all-hats" }} className="hover:text-[#FA5021]">Collections</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[#2A1510]">{meta.title}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-10">
        <p className="mb-8 max-w-2xl font-body text-base text-[#2A1510]/80">{meta.description}</p>

        <div className="grid gap-10 lg:grid-cols-[260px_1fr]">
          <ProductFilters facets={facets} filters={filters} setFilters={setFilters} resultCount={sorted.length} />

          <div>
            <h2 className="mb-4 font-heading text-2xl uppercase tracking-[0.15em] text-[#2A1510] md:text-3xl">Shop {meta.title}</h2>
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#D4CCBC] pb-4">
              <span className="font-ui text-xs font-semibold uppercase tracking-[0.15em] text-[#2A1510]">
                {isLoading ? "Loading…" : `${sorted.length} Result${sorted.length === 1 ? "" : "s"}`}
              </span>

              <div className="flex items-center gap-3">
                <span className="hidden font-ui text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8B6F47] sm:inline">Sort by</span>
                <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
                  <SelectTrigger className="w-[180px] rounded-sm border-[#D4CCBC] bg-[#FDFAF3] font-ui text-xs font-semibold uppercase tracking-wider text-[#2A1510]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FDFAF3]">
                    <SelectItem value="featured">Featured</SelectItem>
                    <SelectItem value="title-asc">Name (A–Z)</SelectItem>
                    <SelectItem value="price-asc">Price: Low to High</SelectItem>
                    <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4">
              <ActiveFilterChips filters={filters} facets={facets} setFilters={setFilters} />
            </div>

            <div className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-3 xl:grid-cols-4">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={i} />)
              ) : sorted.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center gap-3 py-16">
                  <p className="font-heading text-lg uppercase tracking-wider text-[#2A1510]">No products match these filters.</p>
                  <button
                    onClick={() => setFilters({ inStockOnly: false, colors: [], sizes: [], priceRange: [facets.priceMin, facets.priceMax] })}
                    className="btn-cc-accent tracking-[0.2em]"
                  >
                    Clear All Filters
                  </button>
                </div>
              ) : (
                sorted.map((product) => <ProductCard key={product.node.id} product={product} />)
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
